import type {
  KubeEventsLookup,
  KubeLogLookup,
  KubePodsLookup,
  MetricSeriesResponse,
} from "@central-hub/contracts";
import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import type { Session } from "./auth";
import { UsageLine } from "./Charts";
import { EventList, LookupState } from "./ClusterPage";
import { ago, clockSeconds } from "./format";
import { useLookup } from "./hooks";
import { Icon } from "./icons";
import { fmtCores, fmtMemory, type TargetCard, usageChart } from "./model";

const PODS_MS = 15_000;
/** "Follow" re-reads the log this often. */
const FOLLOW_MS = 5_000;
const TAILS = ["100", "200", "500", "1000"] as const;

/**
 * A target's pods, its events and, for whoever may deploy it, a pod's log: read live from the
 * cluster while the detail is open.
 */
export function KubeDetail({
  target,
  session,
  now,
  canReadLogs,
  limits,
}: {
  target: string;
  session: Session;
  now: number;
  /** Logs name visitors: only for those who may deploy the target. */
  canReadLogs: boolean;
  limits?: TargetCard["limits"];
}) {
  const pods = useLookup<KubePodsLookup>(session, "kubernetes", "pods", { target }, PODS_MS);
  const events = useLookup<KubeEventsLookup>(session, "kubernetes", "events", { target }, PODS_MS);

  return (
    <>
      <Usage target={target} session={session} limits={limits} />

      <section className="detail-section">
        <h3 className="eyebrow">Pods</h3>
        <LookupState error={pods.error} loading={!pods.data} />
        {pods.data && pods.data.pods.length === 0 && <p className="muted">No pods.</p>}
        {pods.data && pods.data.pods.length > 0 && (
          <ul className="kube-pods">
            {pods.data.pods.map((p) => {
              const status =
                p.waiting || p.phase === "Failed"
                  ? "down"
                  : p.ready && p.phase === "Running"
                    ? "up"
                    : "degraded";
              return (
                <li key={p.name} className="kube-pod" data-status={status}>
                  <Icon name={status} label={p.waiting ?? p.phase} />
                  <span className="mono ellipsis" title={p.name}>
                    {p.name}
                  </span>
                  <span>{p.waiting ?? (p.ready ? "Ready" : p.phase)}</span>
                  <span className="num muted">
                    {p.startedAt
                      ? `up ${ago(now - Date.parse(p.startedAt)).replace(" ago", "")}`
                      : ""}
                  </span>
                  <span className="num" data-bad={p.restarts > 0}>
                    {p.restarts} restart{p.restarts === 1 ? "" : "s"}
                  </span>
                  <span className="muted ellipsis">{p.node}</span>
                  {p.lastRestart && (
                    <span className="kube-pod-restart muted">
                      Last restart: {p.lastRestart.reason ?? "unknown reason"}
                      {p.lastRestart.exitCode !== undefined && ` (exit ${p.lastRestart.exitCode})`}
                      {p.lastRestart.at && `, ${ago(now - Date.parse(p.lastRestart.at))}`}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {canReadLogs && pods.data && pods.data.pods.length > 0 && (
        <LogViewer
          target={target}
          session={session}
          pods={pods.data.pods.map((p) => ({
            name: p.name,
            containers: p.containers.map((c) => c.name),
          }))}
        />
      )}

      <section className="detail-section">
        <h3 className="eyebrow">Events</h3>
        <LookupState error={events.error} loading={!events.data} />
        {events.data && <EventList events={events.data.events} now={now} />}
      </section>
    </>
  );
}

function LogViewer({
  target,
  session,
  pods,
}: {
  target: string;
  session: Session;
  pods: { name: string; containers: string[] }[];
}) {
  const [pod, setPod] = useState(pods[0]?.name ?? "");
  const [tail, setTail] = useState<(typeof TAILS)[number]>("200");
  const [follow, setFollow] = useState(false);
  const [filter, setFilter] = useState("");
  const current = pods.find((p) => p.name === pod) ?? pods[0];
  const container = current && current.containers.length > 1 ? current.containers[0] : undefined;
  const log = useLookup<KubeLogLookup>(
    session,
    "kubernetes",
    "log",
    current ? { target, pod: current.name, tail, ...(container && { container }) } : null,
    follow ? FOLLOW_MS : 0,
  );
  const box = useRef<HTMLElement>(null);
  const lines = (log.data?.lines ?? []).filter(
    (l) => filter === "" || l.text.toLowerCase().includes(filter.toLowerCase()),
  );

  // Following keeps the newest line in view, like `tail -f`.
  useEffect(() => {
    if (follow && box.current) box.current.scrollTop = box.current.scrollHeight;
  });

  return (
    <section className="detail-section">
      <h3 className="eyebrow">Log</h3>
      <div className="log-controls">
        <label>
          <span className="sr-only">Pod</span>
          <select value={current?.name} onChange={(e) => setPod(e.target.value)}>
            {pods.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="sr-only">Lines</span>
          <select value={tail} onChange={(e) => setTail(e.target.value as (typeof TAILS)[number])}>
            {TAILS.map((t) => (
              <option key={t} value={t}>
                last {t} lines
              </option>
            ))}
          </select>
        </label>
        <input
          type="search"
          placeholder="Filter"
          aria-label="Filter lines"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <label className="choice-inline">
          <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} />
          Follow
        </label>
        <button type="button" className="linkbtn" onClick={log.reload}>
          Refresh
        </button>
      </div>
      <LookupState error={log.error} loading={!log.data} />
      {log.data && (
        <section className="log" ref={box} aria-label={`Log of ${log.data.pod}`}>
          <pre>
            {lines.length === 0 ? (
              <span className="muted">{filter ? "No line matches." : "Nothing logged."}</span>
            ) : (
              lines.map((l, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: a log has no ids; lines only append
                <span key={i} className="log-line">
                  {l.at && <span className="log-at">{clockSeconds(l.at)}</span>}
                  {l.text}
                  {"\n"}
                </span>
              ))
            )}
          </pre>
        </section>
      )}
      {log.data?.truncated && (
        <p className="muted log-note">Older lines are left out: choose more lines to see them.</p>
      )}
    </section>
  );
}

const USAGE_MS = 5 * 60_000;

/** A day of the target's CPU and memory (what its pods use together) against their limits. */
function Usage({
  target,
  session,
  limits,
}: {
  target: string;
  session: Session;
  limits?: TargetCard["limits"];
}) {
  const [series, setSeries] = useState<{
    cpu: MetricSeriesResponse;
    memory: MetricSeriesResponse;
  } | null>(null);
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const token = await session.getToken();
        if (token) {
          const [cpu, memory] = await Promise.all([
            api.metricSeries(token, "kubernetes_target_cpu_cores", "24h"),
            api.metricSeries(token, "kubernetes_target_memory_bytes", "24h"),
          ]);
          if (!stopped) setSeries({ cpu, memory });
        }
      } catch {
        // No chart rather than a broken one: the pods below say the same now.
      }
      if (!stopped) timer = setTimeout(load, USAGE_MS);
    };
    load();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [session]);
  if (!series) return null;
  const charts = [
    usageChart("CPU", series.cpu.series, target, limits?.cpu, fmtCores),
    usageChart("Memory", series.memory.series, target, limits?.memory, fmtMemory),
  ].filter((c) => c !== null);
  if (charts.length === 0) return null;
  return (
    <section className="detail-section">
      <h3 className="eyebrow">Usage, last 24 h</h3>
      <ul className="usage">
        {charts.map((c) => (
          <li key={c.label} className="usage-row">
            <span className="usage-label">{c.label}</span>
            <UsageLine values={c.values} limit={c.limit} bad={(c.ratio ?? 0) >= 0.9} />
            <span className="usage-figures num">
              now <b>{c.now}</b> · peak {c.peak}
              {c.limitText && ` · limit ${c.limitText}`}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
